import { Head } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"

import consumer from "@/channels/consumer"
import InputBar from "@/components/conversation/input-bar"
import MessageList, {
  type MessageData,
} from "@/components/conversation/message-list"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface ConversationProps {
  conversation: {
    id: string
    title: string
    messages: any[]
  }
}

interface BroadcastData {
  id: string
  content?: string
  role?: string
  created_at?: string
  type?: "new_message"
  romanization?: string
  explanation?: any
  translation?: string
  audio_url?: string
  explanation_error?: string
  romanization_error?: string
  translation_error?: string
}

// Module-level buffer to survive component remounts during Inertia visits
const pendingUpdates: Record<string, any> = {}

export default function ConversationShow({ conversation }: ConversationProps) {
  const [messages, setMessages] = useState<MessageData[]>([])
  const [title, setTitle] = useState(conversation.title || conversation.id)
  const [isUploading, setIsUploading] = useState(false)
  const processingRef = useRef(false)

  // Helper to detect language
  const detectLanguage = (text: string): "EN" | "TH" => {
    const hasThai = /[\u0E00-\u0E7F]/.test(text)
    return hasThai ? "TH" : "EN"
  }

  useEffect(() => {
    // Sync title from prop when navigating between existing conversations
    if (conversation.title) {
      setTitle(conversation.title)
    }

    // Transform backend messages to frontend format
    const formattedMessages: MessageData[] = conversation.messages.map(
      (msg: any) => ({
        id: msg.id,
        text: msg.content,
        language: detectLanguage(msg.content),
        timestamp: new Date(msg.created_at),
        translation:
          typeof msg.translation === "object"
            ? msg.translation?.text
            : msg.translation,
        romanization: msg.romanization,
        explanation: msg.explanation,
        audio_url: msg.audio_url,
        explanationError: msg.explanation_error,
        romanizationError: msg.romanization_error,
      }),
    )

    // Guard: don't wipe existing real messages with empty props.
    // This happens when:
    // 1. processingRef is true (fetch in-flight, props not yet updated)
    // 2. After fetch completes, Inertia shared-data re-renders with stale empty props
    setMessages((prevMessages) => {
      const hasRealMessages = prevMessages.some(
        (m) => !String(m.id).startsWith("temp-"),
      )
      if (formattedMessages.length === 0 && hasRealMessages) {
        return prevMessages
      }
      if (formattedMessages.length === 0 && processingRef.current) {
        return prevMessages
      }

      // Snapshot pending updates outside the mapping loop (StrictMode-safe:
      // React may call this updater twice, so we must not mutate pendingUpdates
      // inside the returned mapping function)
      const pendingSnapshot: Record<string, any> = {}
      for (const msg of formattedMessages) {
        if (pendingUpdates[msg.id]) {
          pendingSnapshot[msg.id] = pendingUpdates[msg.id]
          delete pendingUpdates[msg.id]
        }
      }

      return formattedMessages.map((msg) => {
        // 1. Apply pending updates (for new messages that were buffered)
        const pending = pendingSnapshot[msg.id]
        let mergedMsg = msg
        if (pending) {
          mergedMsg = { ...msg, ...pending }
        }

        // 2. Apply preservation logic (for existing messages that were updated via broadcast but prop is stale)
        const currentMsg = prevMessages.find(
          (m) => String(m.id) === String(msg.id),
        )
        if (currentMsg) {
          // If new prop is null/undefined but current state has a value, preserve current state
          // This handles race condition where Inertia reload brings stale DB data between broadcasts
          if (
            (mergedMsg.explanation === null ||
              mergedMsg.explanation === undefined) &&
            currentMsg.explanation
          ) {
            mergedMsg.explanation = currentMsg.explanation
          }
          if (
            (mergedMsg.romanization === null ||
              mergedMsg.romanization === undefined) &&
            currentMsg.romanization
          ) {
            mergedMsg.romanization = currentMsg.romanization
          }
          if (
            (mergedMsg.translation === null ||
              mergedMsg.translation === undefined) &&
            currentMsg.translation
          ) {
            mergedMsg.translation = currentMsg.translation
          }
          if (
            (mergedMsg.audio_url === null ||
              mergedMsg.audio_url === undefined) &&
            currentMsg.audio_url
          ) {
            mergedMsg.audio_url = currentMsg.audio_url
          }
          if (
            (mergedMsg.explanationError === null ||
              mergedMsg.explanationError === undefined) &&
            currentMsg.explanationError
          ) {
            mergedMsg.explanationError = currentMsg.explanationError
          }
          if (
            (mergedMsg.romanizationError === null ||
              mergedMsg.romanizationError === undefined) &&
            currentMsg.romanizationError
          ) {
            mergedMsg.romanizationError = currentMsg.romanizationError
          }
        }
        return mergedMsg
      })
    })

    // Check for pending message from sessionStorage
    const pendingMessage = sessionStorage.getItem(
      `pending_message_${conversation.id}`,
    )
    if (pendingMessage) {
      sessionStorage.removeItem(`pending_message_${conversation.id}`)

      // Optimistically add the message
      const newMessage: MessageData = {
        id: `temp-${Date.now()}`,
        text: pendingMessage,
        language: detectLanguage(pendingMessage),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, newMessage])

      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")

      // Guard: prevent useEffect from wiping temp message during sync processing
      processingRef.current = true

      // Trigger backend creation via fetch to keep socket alive
      fetch("/conversations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken ?? "",
        },
        body: JSON.stringify({
          id: conversation.id,
          first_message: pendingMessage,
        }),
      })
        .then(async (response) => {
          if (!response.ok) {
            console.error("Failed to create conversation")
            return
          }
          const data = await response.json()
          if (data.title) setTitle(data.title)

          // Extract pending BEFORE setMessages (StrictMode-safe: updaters run twice)
          const realId = String(data.message_id)
          const pending = pendingUpdates[realId]
          if (pending) delete pendingUpdates[realId]

          setMessages((prev) =>
            prev.map((msg) => {
              if (msg.id === newMessage.id) {
                let mergedMsg = { ...msg, id: realId }
                if (pending) {
                  mergedMsg = { ...mergedMsg, ...pending }
                }
                return mergedMsg
              }
              return msg
            }),
          )
        })
        .catch((error) => {
          console.error("Error creating conversation:", error)
        })
        .finally(() => {
          processingRef.current = false
        })
    }
  }, [conversation.id, conversation.messages])

  useEffect(() => {
    const subscription = consumer.subscriptions.create(
      { channel: "ConversationChannel", id: conversation.id },
      {
        disconnected: () => {
          // Subscription disconnected
        },
        rejected: () => {
          console.error(
            "Subscription REJECTED by ConversationChannel, id:",
            conversation.id,
          )
        },
        received: (data: BroadcastData) => {
          // Handle new message from audio transcription
          if (data.type === "new_message" && data.content) {
            const newMessage: MessageData = {
              id: data.id,
              text: data.content,
              language: detectLanguage(data.content),
              timestamp:
                data.created_at != null
                  ? new Date(data.created_at)
                  : new Date(),
            }

            setMessages((prevMessages) => {
              // Check if we already have this message (avoid duplicates)
              const exists = prevMessages.some(
                (msg) => String(msg.id) === String(data.id),
              )
              if (exists) {
                return prevMessages
              }

              // Check for temp audio message and replace it instead of removing
              const tempAudioIndex = prevMessages.findIndex((msg) =>
                String(msg.id).startsWith("temp-audio-"),
              )
              if (tempAudioIndex >= 0) {
                return prevMessages.map((msg) =>
                  String(msg.id).startsWith("temp-audio-") ? newMessage : msg,
                )
              }

              // Remove any other temp messages (text messages)
              const withoutTemp = prevMessages.filter(
                (msg) => !String(msg.id).startsWith("temp-"),
              )
              return [...withoutTemp, newMessage]
            })
            return
          }

          // Handle updates to existing messages (translation, audio, explanation, romanization)
          setMessages((prevMessages) => {
            // Check if we have this message in our state
            const messageExists = prevMessages.some(
              (msg) => String(msg.id) === String(data.id),
            )

            if (!messageExists) {
              // Buffer updates for messages not yet in state (race condition: broadcast before fetch response)
              pendingUpdates[data.id] = {
                ...(pendingUpdates[data.id] || {}),
                ...(data.romanization && { romanization: data.romanization }),
                ...(data.explanation && { explanation: data.explanation }),
                ...(data.translation && { translation: data.translation }),
                ...(data.audio_url && { audio_url: data.audio_url }),
                ...(data.explanation_error && {
                  explanationError: data.explanation_error,
                }),
                ...(data.romanization_error && {
                  romanizationError: data.romanization_error,
                }),
              }
              return prevMessages
            }

            // If we do have it, update it immediately
            return prevMessages.map((msg) => {
              const isMatch = String(msg.id) === String(data.id)
              return isMatch
                ? {
                  ...msg,
                  ...(data.romanization && {
                    romanization: data.romanization,
                  }),
                  ...(data.explanation && { explanation: data.explanation }),
                  ...(data.translation && { translation: data.translation }),
                  ...(data.audio_url && { audio_url: data.audio_url }),
                  ...(data.explanation_error && {
                    explanationError: data.explanation_error,
                  }),
                  ...(data.romanization_error && {
                    romanizationError: data.romanization_error,
                  }),
                }
                : msg
            })
          })
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [conversation.id])

  const handleSend = async (text: string) => {
    if (!text.trim()) return

    // Create temp message for optimistic update
    const tempId = `temp-${Date.now()}`
    const newMessage: MessageData = {
      id: tempId,
      text,
      language: detectLanguage(text),
      timestamp: new Date(),
    }
    setMessages((prev) => [...prev, newMessage])

    if (messages.length === 0) {
      // First message - create conversation via fetch (no redirect)
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")

      try {
        processingRef.current = true
        const response = await fetch("/conversations", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken ?? "",
          },
          body: JSON.stringify({
            id: conversation.id,
            first_message: text,
          }),
        })

        if (!response.ok) {
          throw new Error("Failed to create conversation")
        }

        const data = await response.json()
        if (data.title) setTitle(data.title)

        // Extract pending BEFORE setMessages (StrictMode-safe)
        const realId = String(data.message_id)
        const pending = pendingUpdates[realId]
        if (pending) delete pendingUpdates[realId]

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === tempId) {
              let mergedMsg = { ...msg, id: realId }
              if (pending) {
                mergedMsg = { ...mergedMsg, ...pending }
              }
              return mergedMsg
            }
            return msg
          }),
        )
      } catch (error) {
        console.error("Failed to create conversation:", error)
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? { ...msg, text: "Failed to send - tap to retry" }
              : msg,
          ),
        )
      } finally {
        processingRef.current = false
      }
    } else {
      // Subsequent messages - use fetch to keep WebSocket subscription alive
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")

      try {
        processingRef.current = true
        const response = await fetch(
          `/conversations/${conversation.id}/messages`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrfToken ?? "",
            },
            body: JSON.stringify({ content: text }),
          },
        )

        if (!response.ok) {
          throw new Error("Failed to send message")
        }

        const data = (await response.json()) as {
          id: number
          content: string
          created_at: string
        }

        // Extract pending BEFORE setMessages (StrictMode-safe)
        const realId = String(data.id)
        const pending = pendingUpdates[realId]
        if (pending) delete pendingUpdates[realId]

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id === tempId) {
              let mergedMsg = { ...msg, id: realId }
              if (pending) {
                mergedMsg = { ...mergedMsg, ...pending }
              }
              return mergedMsg
            }
            return msg
          }),
        )
      } catch (error) {
        console.error("Failed to send message:", error)
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? { ...msg, text: "Failed to send - tap to retry" }
              : msg,
          ),
        )
      } finally {
        processingRef.current = false
      }
    }
  }

  const handleAudioRecorded = async (blob: Blob) => {
    if (isUploading) return

    setIsUploading(true)

    // Add optimistic "transcribing" message
    const tempId = `temp-audio-${Date.now()}`
    const tempMessage: MessageData = {
      id: tempId,
      text: "Transcribing...",
      language: "EN",
      timestamp: new Date(),
    }
    setMessages((prev) => [...prev, tempMessage])

    try {
      const formData = new FormData()
      formData.append("audio", blob, "recording.webm")

      // Get CSRF token
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")

      const response = await fetch(
        `/conversations/${conversation.id}/audio_messages`,
        {
          method: "POST",
          headers: {
            "X-CSRF-Token": csrfToken ?? "",
          },
          body: formData,
        },
      )

      if (!response.ok) {
        const errorData = (await response.json()) as { error?: string }
        throw new Error(errorData.error ?? "Failed to transcribe audio")
      }

      // Success - ActionCable will broadcast and replace the temp message
      // Keep temp message visible until ActionCable broadcast arrives (prevents message disappearing)
    } catch (error) {
      // Update temp message to show error
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempId
            ? {
              ...msg,
              text:
                error instanceof Error
                  ? error.message
                  : "Failed to send audio",
            }
            : msg,
        ),
      )

      // Remove error message after 3 seconds
      setTimeout(() => {
        setMessages((prev) => prev.filter((msg) => msg.id !== tempId))
      }, 3000)
    } finally {
      setIsUploading(false)
    }
  }

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Conversations",
      href: "/conversations",
    },
    {
      title: title,
      href: `/conversations/${conversation.id}`,
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Conversation" />
      <div className="flex min-h-0 flex-1 flex-col">
        <MessageList messages={messages} />
        <InputBar
          onSend={handleSend}
          onAudioRecorded={(blob) => {
            handleAudioRecorded(blob).catch((error) => {
              console.error("Failed to upload audio:", error)
            })
          }}
          isProcessingAudio={isUploading}
        />
      </div>
    </AppLayout>
  )
}
